// Para Agent's chat bubble (SUP 01, SUP 04): a round button in the
// bottom-right corner of every signed-in page that opens the chat in a panel
// over the page. It sits outside the page the router redraws, so a
// conversation carries on from page to page. There is no Support page: every
// link to #/support opens the bubble instead.
//
// The markup and the open and close rules are plain functions, tested on
// their own. mountAgentDock wires them to the page.

import { esc } from './api';
import type content from './content/sample-content.json';
import { routeFor } from './search';

type Support = (typeof content)['support'];

export const AGENT_PANEL_ID = 'agent-panel';
const AGENT_TITLE_ID = 'agent-title';

// A link that opens the chat, wherever it appears: the menu, the home card,
// the Teleos actions, search results, or an old bookmark.
export function opensAgent(href: string | null | undefined): boolean {
  return !!href && href.startsWith('#') && routeFor(href).page === 'support';
}

// SUP 04: shown in the panel when the agent cannot be reached.
export function agentFallbackHtml(support: Support): string {
  return `<div class="notice" role="alert">
  <p>${esc(support.message)}</p>
  <p><button type="button" data-agent-retry>${esc(support.label)}</button></p>
  <p>${esc(support.teams)}</p>${support.verified ? '' : `\n  <p class="muted">${esc(support.note)}</p>`}
</div>`;
}

const STARTING = '<p class="muted">Starting Para Agent…</p>';

const CHAT_ICON =
  '<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false"><path d="M4 3h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/></svg>';

// The panel is a non-modal dialog: the page stays usable beside it on a wide
// screen. It is hidden until opened, and the bubble says whether it is open.
export function renderAgentDock(): string {
  return `<div class="agent-dock">
  <section id="${AGENT_PANEL_ID}" class="agent-panel" role="dialog" aria-labelledby="${AGENT_TITLE_ID}" hidden>
    <div class="agent-head">
      <h2 id="${AGENT_TITLE_ID}" tabindex="-1">Para Agent</h2>
      <button type="button" class="ghost" data-agent-close aria-label="Close Para Agent">Close</button>
    </div>
    <p class="agent-note">Tickets raised here are real tickets with the technology team.</p>
    <div id="agent" class="agent" aria-live="polite"></div>
  </section>
  <button type="button" class="agent-bubble" data-agent-toggle aria-expanded="false" aria-controls="${AGENT_PANEL_ID}" aria-label="Chat with Para Agent">${CHAT_ICON}</button>
</div>`;
}

export interface DockState {
  open: boolean;
  // Para Agent has been started. It starts on the first open, never before.
  started: boolean;
}

export type DockEvent = 'open' | 'close' | 'toggle' | 'escape';

export interface DockStep {
  state: DockState;
  // Start the agent now, which loads Web Chat and the Copilot Studio client.
  start: boolean;
  // Where focus goes: into the panel, back to what opened it, or nowhere.
  focus: 'panel' | 'opener' | null;
}

export const initialDockState: DockState = { open: false, started: false };

export function dockStep(state: DockState, event: DockEvent): DockStep {
  const opening = event === 'open' || (event === 'toggle' && !state.open);
  if (opening) {
    return { state: { open: true, started: true }, start: !state.started, focus: 'panel' };
  }
  if (!state.open) return { state, start: false, focus: null };
  // Closing hides the panel and keeps the conversation for next time.
  return { state: { ...state, open: false }, start: false, focus: 'opener' };
}

export interface AgentDock {
  open(opener?: HTMLElement | null): void;
  close(): void;
}

// Adds the bubble to the end of host and runs it. start puts Para Agent in
// the element it is given and is called on the first open and on Try again.
export function mountAgentDock(host: HTMLElement, start: (element: HTMLElement, signal: AbortSignal) => Promise<unknown>): AgentDock {
  host.insertAdjacentHTML('beforeend', renderAgentDock());
  const dock = host.lastElementChild as HTMLElement;
  const panel = dock.querySelector<HTMLElement>(`#${AGENT_PANEL_ID}`)!;
  const title = dock.querySelector<HTMLElement>(`#${AGENT_TITLE_ID}`)!;
  const bubble = dock.querySelector<HTMLElement>('[data-agent-toggle]')!;
  const slot = dock.querySelector<HTMLElement>('#agent')!;
  let state = initialDockState;
  let opener: HTMLElement | null = null;
  let run: AbortController | null = null;

  // Web Chat's message box once it is there, otherwise the panel's heading.
  const focusPanel = () => {
    const input = slot.querySelector<HTMLElement>('textarea, input[type="text"]');
    (input ?? title).focus();
  };

  // Each start gets a fresh element, so Try again never draws over the last one.
  const startAgent = () => {
    run?.abort();
    run = new AbortController();
    const element = document.createElement('div');
    element.className = 'agent-run';
    element.innerHTML = STARTING;
    slot.replaceChildren(element);
    // Once the chat is drawn, move from the heading to the message box.
    void start(element, run.signal).then(() => {
      if (state.open && document.activeElement === title) focusPanel();
    });
  };

  const apply = (event: DockEvent, from?: HTMLElement | null) => {
    const step = dockStep(state, event);
    if (step.focus === 'panel' && !state.open) opener = from ?? bubble;
    state = step.state;
    panel.hidden = !state.open;
    bubble.setAttribute('aria-expanded', String(state.open));
    if (step.start) startAgent();
    if (step.focus === 'panel') focusPanel();
    if (step.focus === 'opener') {
      (opener && opener.isConnected ? opener : bubble).focus();
      opener = null;
    }
  };

  bubble.addEventListener('click', () => apply('toggle', bubble));
  dock.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-agent-close]')) apply('close');
    else if (target.closest('[data-agent-retry]')) {
      startAgent();
      title.focus();
    }
  });
  // Capture, so Escape closes the chat even where Web Chat uses the key itself.
  dock.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape' && state.open) {
        e.preventDefault();
        apply('escape');
      }
    },
    true,
  );

  return { open: (from) => apply('open', from), close: () => apply('close') };
}
